import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { useOnboardingStore } from '../store';
import type { ServiceTier, ServiceType } from '../types';
import { AuthContext } from './useAuth';
import { isAdminType } from '../lib/adminRoles';
import { saveOwnOnboardingProfile } from '../lib/onboardingProfile';

interface UserProfileRow {
  admin_type?: string | null;
  business_name?: string | null;
  contact_name?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  onboarding_step?: number | null;
}

interface UserSelectionsRow {
  service_types?: ServiceType[] | null;
  selected_tier?: ServiceTier | null;
  selected_addons?: string[] | null;
}

interface SupabaseSingleResponse<T> {
  data: T | null;
  error: { message?: string } | null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [adminType, setAdminType] = useState<string | null>(null);
  const updateClient = useOnboardingStore((state) => state.updateClient);
  const setStep = useOnboardingStore((state) => state.setStep);
  const setSelectedServiceTypes = useOnboardingStore((state) => state.setSelectedServiceTypes);
  const setSelectedTier = useOnboardingStore((state) => state.setSelectedTier);
  const setSelectedAddOns = useOnboardingStore((state) => state.setSelectedAddOns);
  const reset = useOnboardingStore((state) => state.reset);

  useEffect(() => {
    let mounted = true;

    // Simple auth initialization - NO database calls during init
    const initializeAuth = async () => {
      try {
        // ONLY get the session, no database calls
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!mounted) return;

        if (session?.user) {
          setUser(session.user);
          
          // Load user data ONLY after successful auth, and only once
          await loadUserData(session.user);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    // Load user data separately and handle errors gracefully
    const loadUserData = async (authUser: User) => {
      try {
        await saveOwnOnboardingProfile();

        // Load user data with timeout
        const userPromise = supabase
          .from('users')
          .select('*')
          .eq('id', authUser.id)
          .single();

        const selectionsPromise = supabase
          .from('user_selections')
          .select('*')
          .eq('user_id', authUser.id)
          .maybeSingle();

        // Set a timeout for database calls
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Database timeout')), 5000)
        );

        const [userResponse, selectionsResponse] = await Promise.race([
          Promise.all([userPromise, selectionsPromise]),
          timeoutPromise
        ]) as [
          SupabaseSingleResponse<UserProfileRow>,
          SupabaseSingleResponse<UserSelectionsRow>
        ];

        if (!mounted) return;

        // Handle user data
        if (userResponse.data && !userResponse.error) {
          const userData = userResponse.data;
          setAdminType(userData.admin_type || null);
          if (isAdminType(userData.admin_type)) return;
          updateClient({
            businessName: userData.business_name,
            contactName: userData.contact_name,
            email: userData.email,
            phone: userData.phone,
            address: userData.address
          });
          setStep(userData.onboarding_step || 0);
        }

        // Handle selections data
        if (selectionsResponse.data && !selectionsResponse.error) {
          const selections = selectionsResponse.data;
          if (selections.service_types) {
            setSelectedServiceTypes(selections.service_types);
          }
          if (selections.selected_tier) {
            setSelectedTier(selections.selected_tier);
          }
          if (selections.selected_addons) {
            setSelectedAddOns(selections.selected_addons);
          }
        }
      } catch (error) {
        // Silently handle database errors - don't block the app
        console.log('Could not load user data (non-critical):', error);
      }
    };

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;

        if (session?.user) {
          setUser(session.user);
          // Only load data on sign in, not on every state change
          if (event === 'SIGNED_IN') {
            setIsLoading(true);
            void loadUserData(session.user).finally(() => setIsLoading(false));
          }
        } else {
          setUser(null);
          setAdminType(null);
          reset();
        }
      }
    );

    // Initialize auth
    initializeAuth();

    // Cleanup
    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [reset, setSelectedAddOns, setSelectedServiceTypes, setSelectedTier, setStep, updateClient]);

  return (
    <AuthContext.Provider value={{ user, isLoading, adminType }}>
      {children}
    </AuthContext.Provider>
  );
}
